import { useEffect, useState } from "react";
import { toast } from "sonner";
import { X, Globe, CheckCircle, MagnifyingGlass, DownloadSimple, ArrowsClockwise, WarningCircle } from "@phosphor-icons/react";
import { api, fmtErr } from "../lib/api";

const fmtWhen = (iso) => iso ? new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short" }) : "";

export const SitePeopleDialog = ({ domain, company, keywords, onClose, onCreditsUsed }) => {
  const [loading, setLoading] = useState(true);
  const [res, setRes] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState({});
  const [outcomes, setOutcomes] = useState({});

  const scan = async (force) => {
    setLoading(true);
    setError("");
    if (force) { setRes(null); setOutcomes({}); }
    try {
      const r = await api.siteScanPeople({ domain, company_name: company || "", job_titles: keywords || [], force: !!force });
      if (r.job_id) {
        for (let i = 0; i < 90; i++) {
          await new Promise(ok => setTimeout(ok, 2500));
          const s = await api.siteScanStatus(r.job_id);
          if (s.status === "done") { setRes(s.result); setLoading(false); return; }
          if (s.status === "error") { setError(s.error || "Scan failed"); setLoading(false); return; }
        }
        setError("The scan is taking unusually long — close and reopen this dialog to check again.");
        setLoading(false);
        return;
      }
      setRes(r);
    } catch (err) {
      setError(fmtErr(err));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { scan(false); }, [domain]); // eslint-disable-line react-hooks/exhaustive-deps

  const splitName = (name) => {
    const parts = name.trim().split(/\s+/);
    return [parts[0], parts.slice(1).join(" ")];
  };

  const findEmail = async (p, i) => {
    const [first, last] = splitName(p.name);
    setBusy(prev => ({ ...prev, [i]: true }));
    try {
      const r = await api.hunterFindEmail({ domain, company_name: company || "", first_name: first, last_name: last, position: p.title || "" });
      setOutcomes(prev => ({ ...prev, [i]: r }));
      if (r.imported) toast.success(`${p.name} imported to Contacts — ${r.email}`);
      else if (r.already_in_db) toast.info(`${p.name} is already in MediHub (${r.email})`);
      else if (r.found) toast.warning(`Email found but confidence ${r.score} is below 80 — not imported`);
      else toast.info(`Hunter has no email for ${p.name} — no credit charged`);
      if (r.credits_charged) onCreditsUsed?.();
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setBusy(prev => ({ ...prev, [i]: false }));
    }
  };

  const importFree = async (p, i) => {
    const [first, last] = splitName(p.name);
    setBusy(prev => ({ ...prev, [i]: true }));
    try {
      const r = await api.siteImportPerson({ email: p.email, first_name: first, last_name: last, title: p.title || "", domain, company_name: company || "" });
      setOutcomes(prev => ({ ...prev, [i]: { found: true, email: p.email, imported: r.created === 1, already_in_db: r.already_in_db, credits_charged: 0 } }));
      toast[r.created ? "success" : "info"](r.created ? `${p.name} imported to Contacts — free` : `${p.name} is already in MediHub`);
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setBusy(prev => ({ ...prev, [i]: false }));
    }
  };

  const people = res?.people || [];
  const matchedCount = people.filter(p => p.matched).length;

  return (
    <div className="fixed inset-0 z-50 bg-zinc-950/40 flex items-center justify-center p-4" data-testid="site-people-dialog">
      <div className="bg-white rounded-xl border border-zinc-200 shadow-xl w-full max-w-3xl max-h-[85vh] flex flex-col">
        <div className="px-4 py-3 border-b border-zinc-100 flex items-center gap-2.5">
          <Globe size={18} className="text-zinc-500" />
          <div className="min-w-0">
            <div className="text-sm font-medium text-zinc-900 truncate">{company || domain} — people on their website</div>
            <div className="text-xs text-zinc-400">read from {domain}'s own pages · scanning is completely free</div>
          </div>
          <div className="ml-auto flex items-center gap-1.5">
            {!loading && (
              <button onClick={() => scan(true)} data-testid="site-rescan" title="Re-read the website now"
                className="h-8 px-2.5 rounded-md border border-zinc-200 text-xs font-medium text-zinc-600 hover:border-zinc-400 flex items-center gap-1">
                <ArrowsClockwise size={13} /> Rescan
              </button>
            )}
            <button onClick={onClose} data-testid="site-close" className="h-8 w-8 rounded-md hover:bg-zinc-100 flex items-center justify-center text-zinc-500"><X size={16} /></button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {loading && (
            <div className="p-12 text-center text-zinc-500">
              <MagnifyingGlass size={28} className="mx-auto mb-3 animate-pulse" />
              <p className="text-sm">Reading {domain}'s team, about and leadership pages…</p>
              <p className="text-xs text-zinc-400 mt-1">This usually takes 20–60 seconds. No Hunter credits are used.</p>
            </div>
          )}
          {!loading && error && (
            <div className="p-10 text-center" data-testid="site-scan-error">
              <WarningCircle size={26} className="mx-auto mb-2 text-amber-500" />
              <p className="text-sm text-zinc-700">{error}</p>
              <button onClick={() => scan(true)} className="mt-3 h-8 px-3 rounded-md border border-zinc-200 text-xs font-medium text-zinc-600 hover:border-zinc-400">Try again</button>
            </div>
          )}
          {!loading && !error && res && (
            <>
              <div className="px-4 py-2.5 text-xs text-zinc-500 border-b border-zinc-100 flex flex-wrap items-center gap-x-2 gap-y-1" data-testid="site-scan-summary">
                <span><span className="font-medium text-zinc-900">{people.length}</span> {people.length === 1 ? "person" : "people"} found on {res.pages_scanned?.length || 1} page{(res.pages_scanned?.length || 1) === 1 ? "" : "s"}</span>
                {keywords?.length > 0 && <span>· <span className="font-medium text-emerald-700">{matchedCount}</span> match your keywords</span>}
                {res.emails_on_site?.length > 0 && <span>· {res.emails_on_site.length} email{res.emails_on_site.length === 1 ? "" : "s"} visible on the site (free)</span>}
                {res.cached && (
                  <span className="text-zinc-400">· cached from {fmtWhen(res.scanned_at)} — press Rescan for a fresh read</span>
                )}
              </div>
              <table className="w-full text-sm">
                <thead className="text-xs text-zinc-500 border-b border-zinc-100"><tr>
                  <th className="text-left px-4 py-2 font-medium">Name</th>
                  <th className="text-left px-3 py-2 font-medium">Job title</th>
                  <th className="text-left px-3 py-2 font-medium">Fit</th>
                  <th className="text-left px-3 py-2 font-medium">Email</th>
                  <th className="text-right px-4 py-2 font-medium">Action</th>
                </tr></thead>
                <tbody>
                  {people.map((p, i) => {
                    const out = outcomes[i];
                    const inDb = p.in_db || out?.already_in_db;
                    return (
                      <tr key={p.name + i} className="border-b border-zinc-100 last:border-0" data-testid={`site-person-row-${i}`}>
                        <td className="px-4 py-2 font-medium text-zinc-900 whitespace-nowrap">{p.name}</td>
                        <td className="px-3 py-2 text-zinc-600 max-w-[220px] truncate" title={p.title}>{p.title || "—"}</td>
                        <td className="px-3 py-2">
                          {p.matched
                            ? <span className="inline-block px-1.5 py-0.5 rounded border text-[11px] font-medium bg-emerald-50 text-emerald-700 border-emerald-200">matches keywords</span>
                            : <span className="text-xs text-zinc-300">—</span>}
                        </td>
                        <td className="px-3 py-2 font-mono text-[12px] text-zinc-700">{out?.email || p.email || <span className="text-zinc-300 font-sans">not shown on site</span>}</td>
                        <td className="px-4 py-2 text-right whitespace-nowrap text-xs">
                          {inDb ? (
                            <span className="inline-flex items-center gap-1 text-emerald-700"><CheckCircle size={13} weight="fill" /> in MediHub</span>
                          ) : out?.imported ? (
                            <span className="inline-flex items-center gap-1 text-emerald-700"><CheckCircle size={13} weight="fill" /> imported</span>
                          ) : out && out.found && !out.imported ? (
                            <span className="inline-flex items-center gap-1 text-amber-600" title={`Hunter confidence ${out.score} is below the import cutoff of 80`}><WarningCircle size={13} /> confidence {out.score} — not imported</span>
                          ) : out && !out.found ? (
                            <span className="text-zinc-400">no email found (free)</span>
                          ) : p.email ? (
                            <button onClick={() => importFree(p, i)} disabled={busy[i]} data-testid={`site-import-${i}`}
                              className="h-7 px-2.5 rounded-md bg-zinc-950 text-white font-medium hover:bg-zinc-800 disabled:opacity-40 inline-flex items-center gap-1">
                              <DownloadSimple size={12} />{busy[i] ? "Importing…" : "Import (free)"}
                            </button>
                          ) : (
                            <button onClick={() => findEmail(p, i)} disabled={busy[i]} data-testid={`site-find-email-${i}`}
                              className="h-7 px-2.5 rounded-md border border-zinc-200 font-medium text-zinc-700 hover:border-zinc-400 disabled:opacity-40 inline-flex items-center gap-1">
                              <MagnifyingGlass size={12} />{busy[i] ? "Asking Hunter…" : "Find email (1 credit)"}
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                  {people.length === 0 && (
                    <tr><td colSpan={5} className="p-10 text-center text-sm text-zinc-500">No named people found on {domain}'s public pages — many companies don't list their team online. Try the Hunter search instead.</td></tr>
                  )}
                </tbody>
              </table>
              <p className="px-4 py-3 text-xs text-zinc-400">"Find email" asks Hunter for that exact person — you're only charged 1 credit if an email is found, and it's imported automatically when confidence is 80+. Emails visible on the website import completely free.</p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SitePeopleDialog;
